// Profile modal — unified Profile / Availability / Settings tabs.
// Opens via `root.profileEmail`. Settings + Availability tabs are only
// editable when viewing your own profile (and gated to admin for some
// fields). Profile tab works for any team member.

import {
  formatTzTime,
  currentUserIsAdmin
} from '../../functions/calendar/calendarUtils.js'
const _isViewingSelf = (root) =>
  root?.profileEmail && root.profileEmail === root.currentUserEmail
const _canEditModal = (root) => _isViewingSelf(root) || currentUserIsAdmin(root)
const _formatTime = (tz) => (tz ? formatTzTime(Date.now(), tz) : '')

const _writeUser = (root, patch) => {
  const next = (root.people || []).map((p) =>
    p.email === root.currentUserEmail ? { ...p, ...patch } : p
  )
  root.update({ people: next })
}

// 7×24 availability grid helpers
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const _hhmmToMin = (s) => {
  if (!s) return 0
  const [h, m] = s.split(':').map(Number)
  return h * 60 + m
}

const _isAvailableDefault = (user, hour) => {
  const wh = user?.workingHours
  if (!wh) return true
  const minute = hour * 60
  const start = _hhmmToMin(wh.start)
  const end = _hhmmToMin(wh.end)
  return start < end
    ? minute >= start && minute < end
    : minute >= start || minute < end
}

const _isAvailableAt = (user, day, hour) => {
  if (!user) return false
  const grid = user.availabilityGrid
  if (grid && grid[day] && typeof grid[day][hour] === 'boolean') {
    return grid[day][hour]
  }
  return _isAvailableDefault(user, hour)
}

const _toggleCell = (root, day, hour) => {
  const u =
    (root.people || []).find((p) => p.email === root.profileEmail) || null
  if (!u || !userIsAdmin(u)) return
  const grid = u.availabilityGrid
    ? u.availabilityGrid.map((row) => row.slice())
    : Array.from({ length: 7 }, () =>
        Array.from({ length: 24 }, (_, h) => _isAvailableDefault(u, h))
      )
  grid[day][hour] = !grid[day][hour]
  _writeUser(root, { availabilityGrid: grid })
}

const _setTab = (root, tab) => root.update({ profileModalTab: tab })

export const CalProfileModal = {
  extends: 'Modal',
  stateKey: 'profileEmail',
  extraClose: { profileModalTab: 'profile' },

  Window: {
    width: '100%',
    maxWidth: 'I',
    maxHeight: '90dvh',
    gap: 'A',

    ModalHeader: {
      Avatar: {
        order: -1,
        avatarType: 'initials',
        boxSize: 'D D',
        src: (el, s) =>
          el.call('avatarUrl', el.call('findPerson', s.root.profileEmail)?.name)
      },

      Hgroup: {
        gap: '0',
        H: {
          margin: '0',
          text: (el, s) =>
            el.call('findPerson', s.root.profileEmail)?.name || 'Profile'
        },
        P: {
          text: (el, s) => {
            const p = el.call('findPerson', s.root.profileEmail)
            if (!p) return ''
            const email = String(p.email || s.root.profileEmail || '').toLowerCase()
            const team = (s.root?.teamsByEmail?.[email] || [])[0]?.name
            return [p.position, team].filter(Boolean).join(' · ')
          }
        },
        Email: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          fontFamily: 'monospace',
          text: (el, s) =>
            el.call('findPerson', s.root.profileEmail)?.email || ''
        }
      },
      Icons: {
        ExpandBtn: {
          Icon: { icon: 'expand' },
          title: 'Open as full page',
          onClick: (e, el, s) => {
            e.stopPropagation()
            const email = s.root.profileEmail
            if (!email) return
            s.root.update({ profileEmail: null, profileModalTab: 'profile' })
            let rootEl = el
            while (rootEl && !rootEl.routes) rootEl = rootEl.parent
            const router = el.context?.utils?.router
            if (router && rootEl)
              router(
                `/profile?u=${encodeURIComponent(email)}`,
                rootEl,
                {},
                { pushState: true, scrollToTop: true }
              )
          }
        }
      }
    },

    Tabs: {
      flow: 'x',
      gap: 'V',
      borderBottomWidth: '1px',
      borderBottomColor: 'line',
      borderBottomStyle: 'solid',
      childExtends: 'CalProfileTabBtn',
      children: [
        { tab: 'profile', label: 'Profile' },
        { tab: 'availability', label: 'Availability' },
        {
          tab: 'settings',
          label: 'Settings',
          hide: (el, s) => !_isViewingSelf(s.root)
        }
      ]
    },

    // ── Profile tab ──
    ProfilePane: {
      hide: (el, s) => (s.root.profileModalTab || 'profile') !== 'profile',
      flow: 'y',
      gap: 'A',

      DetailGrid: {
        flow: 'y',
        gap: 'Z',
        padding: 'Z 0',

        childExtends: 'CalProfileDetailRow',
        children: [
          {
            label: 'Their local time',
            valueGetter: (p) => _formatTime(p?.timezone)
          },
          { label: 'GitHub', valueGetter: (p) => p?.github || '—' },
          {
            label: 'Weekly target',
            valueGetter: (p) => String(p?.weeklyHours ?? '—')
          },
          { label: 'Working days', valueGetter: (p) => p?.workingDays || '—' },
          {
            label: 'Working hours',
            valueGetter: (p) => {
              if (!p?.workingHours) return '—'
              return `${p.workingHours.start} – ${p.workingHours.end}`
            }
          }
        ]
      }
    },

    // ── Availability tab ──
    AvailabilityPane: {
      hide: (el, s) => s.root.profileModalTab !== 'availability',
      flow: 'y',
      gap: 'Z',

      Header: {
        flow: 'x',
        align: 'center space-between',

        Title: {
          tag: 'span',
          text: 'General availability',
          fontSize: 'Z2',
          fontWeight: '600',
          color: 'subtitle'
        },
        Hint: {
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          text: (el, s) =>
            currentUserIsAdmin(s.root) ? 'Click to toggle (admin)' : 'Read only'
        }
      },

      CalAvailabilityGrid: {},

      AvailFields: {
        flow: 'y',
        gap: 'Z1',
        padding: 'Z 0',
        childExtends: 'InputField',

        LocationField: {
          Title: { text: 'Location' },
          Input: {
            tag: 'input',
            type: 'text',
            placeholder: 'City, Country',
            readonly: (el, s) => (_canEditModal(s.root) ? null : true),
            value: (el, s) =>
              el.call('findPerson', s.root.profileEmail)?.location || '',
            onInput: (e, el, s) => {
              if (!_canEditModal(s.root)) return
              _writeUser(s.root, { location: e.target.value })
            }
          }
        },

        TimezoneField: {
          Title: { text: 'Timezone (IANA)' },
          Input: {
            tag: 'input',
            type: 'text',
            placeholder: 'e.g. Asia/Tbilisi',
            readonly: (el, s) => (_canEditModal(s.root) ? null : true),
            value: (el, s) =>
              el.call('findPerson', s.root.profileEmail)?.timezone || '',
            onInput: (e, el, s) => {
              if (!_canEditModal(s.root)) return
              _writeUser(s.root, { timezone: e.target.value })
            }
          }
        },

        WeeklyHoursField: {
          Title: { text: 'Weekly hours (auto from grid)' },
          Input: {
            tag: 'input',
            type: 'text',
            readonly: true,
            value: (el, s) => {
              const u = el.call('findPerson', s.root.profileEmail)
              if (!u) return '0'
              let count = 0
              for (let day = 0; day < 7; day++) {
                for (let h = 0; h < 24; h++) {
                  if (_isAvailableAt(u, day, h)) count++
                }
              }
              return `${count} h / week`
            }
          }
        }
      }
    },

    // ── Settings tab (self only) ──
    SettingsPane: {
      hide: (el, s) => s.root.profileModalTab !== 'settings',
      flow: 'y',
      gap: 'Z1',
      childExtends: 'InputField',

      NameField: {
        Title: { text: 'Display name' },
        Input: {
          tag: 'input',
          type: 'text',
          placeholder: 'Your name',
          value: (el, s) =>
            el.call('findPerson', s.root.profileEmail)?.name || '',
          onInput: (e, el, s) => _writeUser(s.root, { name: e.target.value })
        }
      },

      PositionField: {
        Title: { text: 'Position / Title' },
        Input: {
          tag: 'input',
          type: 'text',
          placeholder: 'e.g. Frontend Engineer',
          value: (el, s) =>
            el.call('findPerson', s.root.profileEmail)?.position || '',
          onInput: (e, el, s) =>
            _writeUser(s.root, { position: e.target.value })
        }
      },

      // Read-only team display. Team membership is managed from the
      // profile editor in MemberProfile (with a real picker); the
      // calendar profile popover surfaces it but doesn't edit.
      TeamField: {
        Title: { text: 'Team' },
        Input: {
          tag: 'div',
          padding: 'Z A',
          fontSize: 'A',
          color: 'title',
          text: (el, s) => {
            const p = el.call('findPerson', s.root.profileEmail)
            const email = String(p?.email || s.root.profileEmail || '').toLowerCase()
            return (s.root?.teamsByEmail?.[email] || [])[0]?.name || '—'
          }
        }
      },

      GitHubField: {
        Title: { text: 'GitHub username' },
        Input: {
          tag: 'input',
          type: 'text',
          placeholder: 'e.g. nikoloza',
          value: (el, s) =>
            el.call('findPerson', s.root.profileEmail)?.github || '',
          onInput: (e, el, s) => _writeUser(s.root, { github: e.target.value })
        }
      }
    },

    // ── Footer (always visible) ──
    ModalFooter: {}
  }
}

export const CalProfileTabBtn = {
  tag: 'button',
  type: 'button',
  padding: 'Z A',
  borderWidth: '0',
  borderBottomWidth: '2px',
  borderBottomStyle: 'solid',
  borderBottomColor: (el, s) =>
    (s.root.profileModalTab || 'profile') === el.tab ? 'blue' : 'transparent',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: (el, s) =>
    (s.root.profileModalTab || 'profile') === el.tab ? '600' : '500',
  color: (el, s) =>
    (s.root.profileModalTab || 'profile') === el.tab ? 'title' : 'caption',
  text: (el) => el.label || '',
  marginBottom: '-1px',
  ':hover': { color: 'title' },
  onClick: (e, el, s) => _setTab(s.root, el.tab)
}

export const CalProfileDetailRow = {
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  padding: 'Y 0',

  Label: {
    tag: 'span',
    fontSize: 'Z2',
    color: 'caption',
    width: 'F',
    minWidth: 'F',
    text: (el) => el.parent.label || ''
  },
  Value: {
    tag: 'span',
    fontSize: 'Z2',
    color: 'title',
    flex: '1',
    text: (el, s) => {
      const fn = el.parent.valueGetter
      const p = el.call('findPerson', s.root.profileEmail)
      return typeof fn === 'function' ? fn(p) : ''
    }
  }
}

export const CalProfileUnavailRow = {
  flow: 'x',
  gap: 'Z',
  align: 'center left',
  padding: 'Y Z',
  round: 'Z',
  background: 'red.10',

  TypeBadge: {
    tag: 'span',
    fontSize: 'Y2',
    fontWeight: '700',
    textTransform: 'uppercase',
    color: 'red',
    text: (el, s) => s.type || ''
  },
  Title: {
    tag: 'span',
    fontSize: 'Z2',
    color: 'title',
    flex: '1',
    text: (el, s) => s.title || '(no title)'
  },
  Range: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    fontFamily: 'monospace',
    text: (el, s) => {
      if (s.endDate && s.endDate !== s.date) return `${s.date} → ${s.endDate}`
      return `${s.date} ${s.startTime || ''}–${s.endTime || ''}`
    }
  }
}

// ── Availability grid (used by AvailabilityPane) ──

export const CalAvailabilityGrid = {
  display: 'grid',
  gridTemplateColumns: 'auto repeat(24, 1fr)',
  gridTemplateRows: 'auto repeat(7, auto)',
  gap: '1px',
  background: 'line',
  padding: '1px',
  round: 'Z',
  overflow: 'hidden',
  userSelect: 'none',

  Corner: {
    background: 'modal',
    padding: 'Y'
  },

  HourHeaders: {
    display: 'contents',
    childExtends: 'CalAvailabilityHourHeader',
    childrenAs: 'state',
    children: () => Array.from({ length: 24 }, (_, h) => ({ h }))
  },

  DayRows: {
    display: 'contents',
    childExtends: 'CalAvailabilityDayRow',
    childrenAs: 'state',
    children: () => DAYS.map((label, day) => ({ day, label }))
  }
}

export const CalAvailabilityHourHeader = {
  tag: 'span',
  background: 'modal',
  fontSize: 'Y2',
  color: 'caption',
  fontFamily: 'monospace',
  textAlign: 'center',
  padding: 'Y 0',
  text: (el, s) => String(s.h).padStart(2, '0')
}

export const CalAvailabilityDayRow = {
  display: 'contents',

  Label: {
    tag: 'span',
    background: 'modal',
    fontSize: 'Y2',
    color: 'caption',
    fontWeight: '600',
    padding: 'Y Z',
    align: 'center right',
    text: (el, s) => s.label
  },

  Cells: {
    display: 'contents',
    childExtends: 'CalAvailabilityCell',
    childrenAs: 'state',
    children: (el, s) =>
      Array.from({ length: 24 }, (_, h) => ({ day: s.day, h }))
  }
}

export const CalAvailabilityCell = {
  tag: 'button',
  border: 'none',
  padding: '0',
  height: 'A',
  minHeight: 'A',
  background: (el, s) => {
    const u = el.call('findPerson', el.state.root.profileEmail)
    return _isAvailableAt(u, s.day, s.h) ? 'green' : 'red.15'
  },
  cursor: (el, s) =>
    currentUserIsAdmin(el.state.root) ? 'pointer' : 'not-allowed',
  ':hover': { opacity: '.85' },
  onClick: (e, el, s) => {
    e.preventDefault()
    _toggleCell(el.state.root, s.day, s.h)
  }
}
